
  import { computed, ComputedRef, defineComponent, ref, toRef, getCurrentInstance, watch, reactive } from 'vue';
  import DropdownModal from '@/_legacy/views/ConductInspection/Modals/DropdownModal.vue';
  import NotesIcon from '@/_legacy/components/Icons/NotesIcon.vue';
  import CameraIcon from '@/_legacy/components/Icons/CameraIcon.vue';
  import RaiseIssueModal from '@/_legacy/views/ConductInspection/Modals/RaiseIssueModal.vue';
  import { useQuestionOptions } from '@/ui/concerns/conductInspection/useQuestionOptions';
  import { useEntityTypeOfResponse } from '@/ui/concerns/conductInspection/useEntityTypeOfResponse';
  import { retrieveEntityItems } from '@/services/entityItem/entityItemService';
  import { EntityItemList } from '@/entities/entityItem';
  import { defaultEntityItemQuery, EntityItemQuery } from '@/services/api/resources/entityItem/query';
  import { useTask } from '@/ui/concerns';
  import CommentButton from '@/_legacy/components/ConductInspection/buttons/CommentButton.vue';
  import PhotoButton from '@/_legacy/components/ConductInspection/buttons/PhotoButton.vue';
  import ActionButton from '@/_legacy/components/ConductInspection/buttons/ActionButton.vue';
  import { DropdownField, TagField } from '@/ui/components/form';
  import { SelectOptions } from '@/ui/types';
  import { INSPECTION_STATUS } from '@/_legacy/constants';
  import { debounce } from 'lodash';

  interface EntityItemTypeOfResponseProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any;
  }

  export default defineComponent<EntityItemTypeOfResponseProps>({
    components: {
      DropdownField,
      TagField,
      ActionButton,
      PhotoButton,
      CommentButton,
      RaiseIssueModal,
      NotesIcon,
      CameraIcon
    },
    props: ['item'],
    setup(props) {
      const instance = getCurrentInstance();
      const questionItem = toRef(props, 'item');

      const {
        dropdownVisible,
        limit,
        multiple,
        inspection,
        showDropdown,
        closeDropdown,
      } = useEntityTypeOfResponse(props.item);

      const {
        isRaiseIssueModalVisible,
        showNotes,
        showPhotos,
        raiseIssueButtonClicked,
        raiseIssueModalClosed,
        containsPhotos,
        containsIssues,
        raisedIssues,
        notesIconFill,
        photosIconFill,
        issuesAllowed,
        raiseIssueButtonClasses,
      } = useQuestionOptions(questionItem, inspection, instance);

      const issuesDisabled = computed(() => {
        if (!instance?.proxy) {
          return true;
        }
        const { inspection_id, status } = instance.proxy.$store.state.conduct.inspection;

        return inspection_id && status === INSPECTION_STATUS.DONE && raisedIssues.value;
      });

      const entityItems = ref<SelectOptions>([]);
      const baseQuery = reactive(defaultEntityItemQuery());
      const query = computed(() => {
        return {
          ...baseQuery,
          entityType: props.item.entity_types,
          entityFolderRecursive: props.item.entity_folders,
        } as EntityItemQuery;
      });

      const debouncedSearch = debounce((value: string) => {
        baseQuery.search = value;
        baseQuery.page = 1;
      }, 300);

      const filterEntityItems = (value: string) => {
        if (value.length > 2) {
          debouncedSearch(value);

          return;
        }
        debouncedSearch.cancel?.();
        baseQuery.search = '';
        baseQuery.page = 1;
      };

      const fetchEntityItemsTask = useTask(
        () => retrieveEntityItems(query.value),
        (resp: EntityItemList) => {
          entityItems.value = resp.data.map(row => {
            return {
              value: row.id,
              label: row.data.title
            };
          }).sort((a, b) => {
            if (a.label < b.label) { return -1; }
            if (a.label > b.label) { return 1; }
            return 0;
          });
        }
      );

      watch(query, fetchEntityItemsTask.run, {
        immediate: true,
      });

      const selectedAnswers: ComputedRef<number[]> = computed(() => {
        return props.item.response || [];
      });

      const entityItemSelected = (entityItemId: number[]|null) => {
        instance?.proxy?.$store.commit('conduct/changeResponse', {
          item: props.item.uuid,
          response: entityItemId?.flatMap(f => f || []),
          responded: Boolean(entityItemId && entityItemId.length),
        });

        const normalizedEntityItemId = entityItemId && entityItemId.length ? entityItemId[0] ?? null : null;
        const headerEntityItemQuestionUuid = instance?.proxy?.$store.state.conduct.headerEntityItemQuestionUuid;
        const isHeaderEntityItem = props.item.uuid === headerEntityItemQuestionUuid;

        // Only dispatch entityItemChanged (which triggers prefill) if this is the header entity item question
        if (isHeaderEntityItem) {
          instance?.proxy?.$store.dispatch('conduct/entityItemChanged', {
            entityItemId: normalizedEntityItemId ?? null,
          });
        }

        closeDropdown();
      };

      return {
        selectedAnswers,
        dropdownVisible,
        limit,
        multiple,
        inspection,
        entityItems,
        entityItemSelected,
        showDropdown,
        closeDropdown,
        showNotes,
        showPhotos,
        isRaiseIssueModalVisible,
        raiseIssueButtonClicked,
        raiseIssueModalClosed,
        containsPhotos,
        containsIssues,
        notesIconFill,
        photosIconFill,
        raiseIssueButtonClasses,
        issuesAllowed,
        issuesDisabled,
        filterEntityItems,
      };
    }
  });
